'use client';

import React from 'react';
import {
  IRegionCam, IRegionResortsPaginated, IRegionResortsPerStatus, IRegionShort, IRegionStats,
  IRegionFeaturedResort,
} from '@/api/region/types';
import {
  IRelatedNews, OpenFlag, IResortPathInfo, IDescriptionsResort, IResortPageSnowInfo,
} from '@/api/resort/types';
import { I18nKey } from '@/i18n/types';
import { IFeaturedResort } from '@/api/home/types';
import { Hero400Error } from '@/components/400';
import { FeaturedResort } from '@/components/home/FeaturedResort';
import PlanYourTrip from '@/components/common/PlanYourTrip/RegionWidget';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { useRegionLayoutBubble } from '@/page-components/region/_context';
import { IRegionLayout } from '@/page-components/region/_layout';
import { Routes } from '@/routes';
import { formatAgoTimestampOrString } from '@/util/formatAgo';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { ResortWebcam } from '@/components/common/ResortWebcam';
import { SeoSection } from '@/components/common/SeoSection';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import { formatPercentVal } from '@/util/formatPercent';
import { spansMultipleRegions } from '@/util/spansMultipleRegions';
import {
  OpenResortsStatsTable,
  ProjectedOpeningTable,
  PermClosedList,
} from '@/components/region/StatusTables';
import { LoadMoreRows } from '@/components/region/LoadMoreRows';
import { useLoadMoreResorts } from '@/components/region/useLoadMoreResorts';
import { fetchRegionResortsByStatus } from '@/api/region/static';
import { REGION_RESORT_PAGE_SIZE } from '@/ots-constants';

export type RegionSkiReportPageProps = {
  resorts: Partial<IRegionResortsPerStatus>,
  featured: IRegionFeaturedResort,
  featuredResortByIndex: IResortPageSnowInfo | null,
  featuredCam: IRegionCam,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  stat: IRegionStats,
  regionResortListGuide: Array<IResortPathInfo>,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-skireport-page';
export const TEMPLATE_ID = '2.1.1';
export const TEMPLATE_NAME = 'Region Snow Report';

// Production-aligned section order. Open and WeekendsOnly carry live
// conditions; the closed buckets surface for completeness (their cells
// degrade to a dash when data isn't populated). PermanentlyClosed
// stays here — there's a separate DB flag that suppresses these
// resorts at the source when we don't want to surface them.
const STATUS_SECTIONS: { flag: OpenFlag; labelKey: I18nKey['common'] }[] = [
  { flag: OpenFlag.Open, labelKey: 'resort.skiReport.open' },
  { flag: OpenFlag.WeekendsOnly, labelKey: 'resort.skiReport.weekendsOnly' },
  { flag: OpenFlag.TemporarilyClosed, labelKey: 'resort.skiReport.temporarilyClosed' },
  { flag: OpenFlag.NoReportAvailable, labelKey: 'resort.skiReport.noReportAvailable' },
  { flag: OpenFlag.Closed, labelKey: 'resort.skiReport.closed' },
  { flag: OpenFlag.PermanentlyClosed, labelKey: 'resort.skiReport.permanentlyClosed' },
];

// One status group. Each owns its own paging state, so a region whose
// "Closed" bucket runs to 409 rows (476/Europe) can page through it while the
// smaller groups on the same page sit complete and untouched.
const StatusSection = ({
  flag, labelKey, initial, regionId, showRegion,
}: {
  flag: OpenFlag;
  labelKey: I18nKey['common'];
  initial: IRegionResortsPaginated;
  regionId: string;
  showRegion: boolean;
}) => {
  const { t } = useOTSTranslation('common');

  const fetchPage = React.useCallback(
    (page: number) => fetchRegionResortsByStatus(regionId, flag, page, REGION_RESORT_PAGE_SIZE),
    [regionId, flag],
  );

  const {
    rows, hasMore, isLoading, loadMore, loadAll,
  } = useLoadMoreResorts({
    initial,
    fetchPage,
    label: `region ${regionId} skireport ${flag}`,
  });

  return (
    <section>
      <Heading
        level={2}
        className="mb-3 font-heading text-lg text-secondary md:text-xl"
      >
        {t(labelKey)}
      </Heading>
      {flag === OpenFlag.PermanentlyClosed && (
        <PermClosedList rows={rows} showRegion={showRegion} />
      )}
      {flag === OpenFlag.Closed && (
        <ProjectedOpeningTable
          rows={rows}
          linkTo={(r) => Routes.resortSnowReport(r)}
          dateLabel={t('resort.skiReport.projectedOpening')}
          showRegion={showRegion}
          onSortIntent={loadAll}
          isLoading={isLoading}
        />
      )}
      {flag !== OpenFlag.PermanentlyClosed && flag !== OpenFlag.Closed && (
        <OpenResortsStatsTable
          rows={rows}
          showRegion={showRegion}
          onSortIntent={loadAll}
          isLoading={isLoading}
        />
      )}
      <LoadMoreRows hasMore={hasMore} isLoading={isLoading} onLoadMore={loadMore} />
    </section>
  );
};

export default function RegionSkiReportPage({
  resorts, pathInfo, featuredCam, numResorts,
  featuredResortByIndex,
  relatedNews,
  stat,
  regionResortListGuide,
  featuredResortDescriptionTabs,
}: RegionSkiReportPageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');

  useRegionLayoutBubble({});

  if (!resorts) {
    return <Hero400Error light />;
  }

  const featuredResort: IFeaturedResort | null = featuredResortByIndex ? {
    uuid: featuredResortByIndex.uuid,
    title: featuredResortByIndex.title,
    region: featuredResortByIndex.region,
    image: featuredResortByIndex.image,
    smallImage: featuredResortByIndex.smallImage ?? '',
    video: featuredResortByIndex.video ?? null,
    website: featuredResortByIndex.website ?? null,
    phone: featuredResortByIndex.phone ?? null,
    isPartner: featuredResortByIndex.isPartner,
    twitter: featuredResortByIndex.twitter ?? null,
    liftsUrl: featuredResortByIndex.liftsUrl ?? null,
    rentalUrl: featuredResortByIndex.rentalUrl ?? null,
    lessonsUrl: featuredResortByIndex.lessonsUrl ?? null,
    socialMediaAccounts: featuredResortByIndex.socialMediaAccounts ?? [],
  } : null;

  const seoTitle = pathInfo.isLogical ? t('region.skiReport.logicSeoTitle', { region: pathInfo.title })
    : t('region.skiReport.seoTitle', { region: pathInfo.title });

  const seoText = pathInfo.isLogical ? m('region.skiReport.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    percent_depth_avg: formatPercentVal(stat.depthsPercent.base, ''),
    avgsnowfall: dt(stat.averageSnowfallCm),
  }) : m('region.skiReport.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    percent_depth_avg: formatPercentVal(stat.depthsPercent.base, ''),
    avgsnowfall: dt(stat.averageSnowfallCm),
  });

  // Pre-compute which sections will render so we can stack cleanly. Each
  // keeps its whole paginated response — the section owns paging from there.
  const renderable = STATUS_SECTIONS
    .map((s) => ({ ...s, response: resorts[s.flag] }))
    .filter((s) => (s.response?.data?.length ?? 0) > 0);

  // Logical regions (Ikon Pass, Epic Pass, …) list resorts from several
  // primary regions, so the region name is what disambiguates the row.
  // Decided once across every rendered section: a resort under "Open" and
  // one under "Closed" should read the same way on the same page. Judged on
  // the server-rendered rows so later pages cannot flip it mid-scroll.
  const showRegion = spansMultipleRegions(renderable.flatMap((s) => s.response?.data ?? []));

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection
            title={seoTitle}
            link={{
              href: Routes.regionCams(pathInfo),
              label: t('region.skiReport.webcamSnowReport'),
            }}
          >
            {seoText}
          </SeoSection>

          {renderable.length > 0 ? (
            renderable.map((section) => (
              <StatusSection
                key={section.flag}
                flag={section.flag}
                labelKey={section.labelKey}
                initial={section.response as IRegionResortsPaginated}
                regionId={pathInfo.uuid}
                showRegion={showRegion}
              />
            ))
          ) : (
            <Text variant="default" className="text-muted-foreground">
              {t('common.na')}
            </Text>
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
          />

          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />

          {featuredResort && (
            <FeaturedResort
              resort={featuredResort}
              regionResorts={regionResortListGuide}
              resortDescriptionTabs={featuredResortDescriptionTabs ?? undefined}
              isOnRegionExceptSkiResorts
              utmCampaign="region_widget"
            />
          )}
        </PageBody>
      </Main>
      <Side>
        {featuredCam && (
          <ResortWebcam
            showResort
            resortName={featuredCam.resort.title}
            caption={featuredCam.webcam.title}
            image={featuredCam.webcam.image}
            timestamp={formatAgoTimestampOrString(featuredCam.webcam.date)}
            href={Routes.resortCams(featuredCam.resort)}
            aspect="4/3"
          />
        )}
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd id={pathInfo.uuid} isRegion hideWeSkiOnMobile />
      </Side>
    </>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
